/**
 * useEntityEvents - Hook for fetching and grouping entity lifecycle events
 *
 * Aggregates events from multiple sources:
 * - Vehicle: maintenance, trips, inspections, fuel_logs, claims, documents
 * - Driver: trips, documents
 *
 * Groups events by time period:
 * - Ce mois (current month)
 * - Mois précédent (last month)
 * - Plus ancien (older)
 *
 * @param {string} entityType - 'vehicle' | 'driver'
 * @param {number|string} entityId - The entity ID
 * @returns {Object} Events data and handlers
 */
import { useState, useEffect, useCallback, useMemo } from 'react';
import api, { documentAPI } from '../api.js';
import { formatMoney } from '../utils/formatMoney.js';

// Event type configuration
export const EVENT_TYPES = {
  maintenance: {
    icon: 'Wrench',
    color: 'amber',
    label: 'Maintenance',
    getDate: (e) => e.created_at || e.request_date,
    getTitle: (e) => e.description || `Maintenance ${e.maintenance_type}`,
    getStatus: (e) => e.status
  },
  assignment: {
    icon: 'ArrowsRightLeft',
    color: 'blue',
    label: 'Affectation',
    getDate: (e) => e.start_date || e.assigned_at,
    getTitle: (e) => e.purpose || 'Affectation',
    getStatus: (e) => e.status
  },
  inspection: {
    icon: 'ClipboardCheck',
    color: 'emerald',
    label: 'Inspection',
    getDate: (e) => e.inspection_date || e.created_at,
    getTitle: (e) => e.inspection_type || 'Inspection',
    getStatus: (e) => e.status
  },
  fuel: {
    icon: 'Fuel',
    color: 'orange',
    label: 'Carburant',
    getDate: (e) => e.fuel_date,
    // No hardcoded "€" — a fuel log belongs to an org, whose currency may be
    // PLN/SEK. `currency` is optional: pass the org's currency (e.g. via
    // useOrgCurrency().currencyOf(event)) from the caller when available.
    // Without it, the amount renders as a plain number (never a wrong symbol).
    getTitle: (e, currency) => currency
      ? `${e.liters || 0}L - ${formatMoney(e.total_cost || 0, currency)}`
      : `${e.liters || 0}L - ${e.total_cost || 0}`,
    getStatus: () => 'completed'
  },
  claim: {
    icon: 'AlertTriangle',
    color: 'red',
    label: 'Sinistre',
    getDate: (e) => e.event_date || e.declared_at,
    getTitle: (e) => e.description || `Sinistre ${e.type}`,
    getStatus: (e) => e.status
  },
  document: {
    icon: 'FileText',
    color: 'slate',
    label: 'Document',
    getDate: (e) => e.uploaded_at || e.created_at,
    getTitle: (e) => e.doc_type || e.document_type || 'Document',
    getStatus: (e) => e.validation_status || 'pending'
  },
  location: {
    icon: 'MapPin',
    color: 'green',
    label: 'Emplacement',
    getDate: (e) => e.assigned_at,
    getTitle: (e) => `Affectation ${e.assignment_type || 'site'}`,
    getStatus: (e) => e.is_current ? 'active' : 'completed'
  }
};

// Time period grouping
function getTimePeriod(date) {
  if (!date) return 'older';

  const eventDate = new Date(date);
  const now = new Date();
  const thisMonth = new Date(now.getFullYear(), now.getMonth(), 1);
  const lastMonth = new Date(now.getFullYear(), now.getMonth() - 1, 1);

  if (eventDate >= thisMonth) {
    return 'current';
  } else if (eventDate >= lastMonth) {
    return 'previous';
  }
  return 'older';
}

// Period labels
export const PERIOD_LABELS = {
  current: 'Ce mois',
  previous: 'Mois précédent',
  older: 'Plus ancien'
};

export function useEntityEvents(entityType, entityId) {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Fetch all events for the entity
  const fetchEvents = useCallback(async () => {
    if (!entityId) return;

    setLoading(true);
    setError(null);

    try {
      const allEvents = [];

      if (entityType === 'vehicle') {
        // Fetch vehicle-related events in parallel
        const [maintenanceRes, tripsRes, inspectionsRes, fuelRes, claimsRes, locationsRes, docsRes] = await Promise.all([
          api.maintenance.getAll().catch(() => ({ data: [] })),
          api.trip.getAll().catch(() => ({ data: [] })),
          api.inspection?.getAll?.().catch(() => ({ data: [] })) || Promise.resolve({ data: [] }),
          api.fuelLog?.getAll?.().catch(() => ({ data: [] })) || Promise.resolve({ data: [] }),
          api.claim?.getAll?.().catch(() => ({ data: [] })) || Promise.resolve({ data: [] }),
          api.vehicleLocation?.getByVehicle?.(entityId).catch(() => ({ data: [] })) || Promise.resolve({ data: [] }),
          documentAPI.getForVehicle?.(entityId).catch(() => ({ data: [] })) || Promise.resolve({ data: [] })
        ]);

        // Filter and map maintenance requests
        const maintenance = (maintenanceRes.data || [])
          .filter(m => m.vehicle_id === entityId)
          .map(m => ({
            ...m,
            eventType: 'maintenance',
            eventDate: EVENT_TYPES.maintenance.getDate(m)
          }));
        allEvents.push(...maintenance);

        // Filter and map trips (assignments)
        const trips = (tripsRes.data || [])
          .filter(t => t.vehicle_id === entityId)
          .map(t => ({
            ...t,
            eventType: 'assignment',
            eventDate: EVENT_TYPES.assignment.getDate(t)
          }));
        allEvents.push(...trips);

        // Filter and map inspections
        const inspections = (inspectionsRes.data || [])
          .filter(i => i.vehicle_id === entityId)
          .map(i => ({
            ...i,
            eventType: 'inspection',
            eventDate: EVENT_TYPES.inspection.getDate(i)
          }));
        allEvents.push(...inspections);

        // Filter and map fuel logs
        const fuelLogs = (fuelRes.data || [])
          .filter(f => f.vehicle_id === entityId)
          .map(f => ({
            ...f,
            eventType: 'fuel',
            eventDate: EVENT_TYPES.fuel.getDate(f)
          }));
        allEvents.push(...fuelLogs);

        // Filter and map claims
        const claims = (claimsRes.data || [])
          .filter(c => c.vehicle_id === entityId)
          .map(c => ({
            ...c,
            eventType: 'claim',
            eventDate: EVENT_TYPES.claim.getDate(c)
          }));
        allEvents.push(...claims);

        // Map location assignments
        const locations = (locationsRes.data || [])
          .map(l => ({
            ...l,
            eventType: 'location',
            eventDate: EVENT_TYPES.location.getDate(l)
          }));
        allEvents.push(...locations);

        // Map documents
        const docs = (docsRes.data || [])
          .map(d => ({
            ...d,
            eventType: 'document',
            eventDate: EVENT_TYPES.document.getDate(d)
          }));
        allEvents.push(...docs);

      } else if (entityType === 'driver') {
        // Fetch driver-related events in parallel
        const [tripsRes, docsRes] = await Promise.all([
          api.trip.getAll().catch(() => ({ data: [] })),
          documentAPI.getForDriver?.(entityId).catch(() => ({ data: [] })) || Promise.resolve({ data: [] })
        ]);

        // Filter and map trips
        const trips = (tripsRes.data || [])
          .filter(t => t.driver_id === entityId)
          .map(t => ({
            ...t,
            eventType: 'assignment',
            eventDate: EVENT_TYPES.assignment.getDate(t)
          }));
        allEvents.push(...trips);

        // Map documents
        const docs = (docsRes.data || [])
          .map(d => ({
            ...d,
            eventType: 'document',
            eventDate: EVENT_TYPES.document.getDate(d)
          }));
        allEvents.push(...docs);
      }

      // Sort events by date (most recent first)
      allEvents.sort((a, b) => {
        const dateA = new Date(a.eventDate || 0);
        const dateB = new Date(b.eventDate || 0);
        return dateB - dateA;
      });

      setEvents(allEvents);
    } catch (err) {
      console.error('Error fetching entity events:', err);
      setError(err.message || 'Failed to load events');
    } finally {
      setLoading(false);
    }
  }, [entityType, entityId]);

  // Auto-fetch on mount and when entity changes
  useEffect(() => {
    fetchEvents();
  }, [fetchEvents]);

  // Group events by time period
  const groupedEvents = useMemo(() => {
    const groups = {
      current: { label: PERIOD_LABELS.current, events: [], byType: {} },
      previous: { label: PERIOD_LABELS.previous, events: [], byType: {} },
      older: { label: PERIOD_LABELS.older, events: [], byType: {} }
    };

    // Initialize byType for each period
    Object.keys(groups).forEach(period => {
      Object.keys(EVENT_TYPES).forEach(type => {
        groups[period].byType[type] = [];
      });
    });

    // Group events
    events.forEach(event => {
      const period = getTimePeriod(event.eventDate);
      groups[period].events.push(event);
      if (groups[period].byType[event.eventType]) {
        groups[period].byType[event.eventType].push(event);
      }
    });

    return groups;
  }, [events]);

  // Event counts by type
  const eventCounts = useMemo(() => {
    const counts = {};
    Object.keys(EVENT_TYPES).forEach(type => {
      counts[type] = events.filter(e => e.eventType === type).length;
    });
    counts.total = events.length;
    return counts;
  }, [events]);

  return {
    events,
    groupedEvents,
    eventCounts,
    loading,
    error,
    reload: fetchEvents
  };
}

export default useEntityEvents;
